import React from 'react';
import CampaignTile from './CampaignTile';
import { DEFAULT_CHART_SETTINGS, type MetricRow } from '../../../../support/campaignAnalytics';
import { GROUP_STATUS_DELIM } from '../../../../support/campaignSalesLevels';

/**
 * A nested (hierarchical) donut, composed from two concentric {@link CampaignTile} donuts because the
 * Harbor chart catalogue has no native nested donut. The two rings are a parent→child hierarchy of the
 * **same population**, not two independent totals:
 *  - **outer** ring = the current sales-level grouping (Sales Level 1 at Global, Sales Level 2 once a
 *    level-1 is selected), whose wedge names render as arc-link labels around the outside;
 *  - **inner** ring = each outer group's slice subdivided by completion status (Not Started / In
 *    Progress / Completed), aligned angularly under its parent group's outer wedge.
 * The inner rows arrive already ordered by group (then status) and each group's status slices sum to
 * that group's outer value, so — both donuts drawing in data order from the same start angle — the
 * inner status slices sit under their parent group's outer wedge. The two rings are rendered with the
 * **same radial width**, the inner ring's outer edge flush against the outer ring's inner edge; the
 * inner donut's center shows the total. Inner slices are keyed `Group · Status` so each stays unique
 * for the renderer (it keys slices by label), but the tooltip strips the `Group · ` prefix — a hover
 * reads just the status + count (the group is already named by the outer ring).
 *
 * Reuses the proven inline tile renderer so it looks and behaves like every other analytics chart.
 */
interface Props {
  /** Inner-ring rows — each group's status sub-slices, in group-then-status order. */
  inner: MetricRow[];
  /** Outer-ring rows — the sales-level group split. */
  outer: MetricRow[];
  /** Human label for the outer grouping dimension, e.g. "Sales Level 1". */
  groupDimensionLabel: string;
  /** What each slice's value counts — the tooltip's value-row key. Defaults to "Enrollments". */
  valueTitle?: string;
  /** Stable, unique key prefix for the two synthetic tiles. */
  tileKey: string;
  height?: number;
  empty?: string;
}

const nonZero = (rows: MetricRow[]): MetricRow[] => rows.filter((r) => (r.value ?? 0) > 0);

const TwoRingDonut: React.FC<Props> = ({
  inner,
  outer,
  groupDimensionLabel,
  valueTitle = 'Enrollments',
  tileKey,
  height = 300,
  empty = 'No enrollments in scope.',
}) => {
  const innerRows = nonZero(inner);
  const outerRows = nonZero(outer);
  const total = inner.reduce((s, r) => s + (r.value ?? 0), 0);

  if (total <= 0) {
    return (
      <div
        className="hbr-css__text-secondary-sm"
        style={{ height, minHeight: height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        {empty}
      </div>
    );
  }

  // Geometry: draw the inner ring with the SAME band width as the outer ring, its outer edge flush
  // against the outer ring's inner edge. The outer donut fills `height` (less its label margin) and
  // its band spans OUTER_INNER_RADIUS..1.0 of that radius; we size + set the inner donut so its band
  // occupies the equal-width slot immediately inside.
  const OUTER_MARGIN = 24;
  const INNER_MARGIN = 6;
  const OUTER_INNER_RADIUS = 0.78;
  const outerRadius = height / 2 - OUTER_MARGIN;
  const bandWidth = outerRadius * (1 - OUTER_INNER_RADIUS);
  const innerOuterRadius = outerRadius * OUTER_INNER_RADIUS; // == outer ring's inner edge
  const innerSize = Math.round(2 * (innerOuterRadius + INNER_MARGIN));
  const innerInnerRadius = (innerOuterRadius - bandWidth) / innerOuterRadius; // equal band width

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ position: 'relative', width: '100%', height }}>
        {/* Outer ring — the grouping dimension; arc-link labels name each wedge. */}
        <div style={{ position: 'absolute', inset: 0 }}>
          <CampaignTile
            chartType="donut_chart"
            data={outerRows}
            height={height}
            tileKey={`${tileKey}-outer`}
            settings={{
              ...DEFAULT_CHART_SETTINGS,
              innerRadius: 0.78,
              centerMode: 'none',
              show_legend: false,
              enableArcLinkLabels: true,
              marginTop: 24,
              marginBottom: 24,
              marginLeft: 24,
              marginRight: 24,
              // Tooltip keyed to what the outer slice actually is: a group and its enrollment count
              // (labelValue mode drops the redundant "Series" row — the slice id equals its label here).
              tooltipMode: 'labelValue',
              tooltipLabelTitle: groupDimensionLabel,
              tooltipValueTitle: valueTitle,
            }}
          />
        </div>
        {/* Inner ring — each group's status sub-slices; same band width, flush inside the outer ring. */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: innerSize,
            height: innerSize,
            transform: 'translate(-50%, -50%)',
          }}
        >
          <CampaignTile
            chartType="donut_chart"
            data={innerRows}
            height={innerSize}
            tileKey={`${tileKey}-inner`}
            settings={{
              ...DEFAULT_CHART_SETTINGS,
              innerRadius: innerInnerRadius,
              centerMode: 'total',
              show_legend: false,
              marginTop: 6,
              marginBottom: 6,
              marginLeft: 6,
              marginRight: 6,
              // Each inner slice is a group's status sub-slice, uniquely keyed as `Group · Status` for
              // slice identity; the tooltip strips the `Group · ` prefix so a hover reads just the status.
              tooltipMode: 'labelValue',
              tooltipLabelTitle: 'Status',
              tooltipLabelDelimiter: GROUP_STATUS_DELIM,
              tooltipValueTitle: valueTitle,
            }}
          />
        </div>
      </div>
      <div className="hbr-css__text-secondary-xs" style={{ textAlign: 'center' }}>
        Outer ring: {groupDimensionLabel} · Inner ring: completion status
      </div>
    </div>
  );
};

export default TwoRingDonut;
